// ── Provider Availability ────────────────────────────────────────────────────
// Lets the provider configure: available weekdays, partial-day windows per
// weekday (e.g. Friday mornings only), and additional within-day blocked times
// (meetings, lunch, admin). The wizard reads provider.availability and uses it
// as a hard constraint alongside school + grade + flex-day rules.
import { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FloppyDisk, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { nativeOptions } from "@/lib/nativeOptions";
import { DayOfWeekMultiPicker } from "@/components/schedule/DayOfWeekMultiPicker";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri"];

export default function ProviderAvailabilityCard() {
  const [provider, setProvider] = useState(null);
  const [draft, setDraft] = useState({
    available_days: [0, 1, 2, 3, 4],
    day_windows: {}, // { "0": { start, end } }
    blocked_times: [], // [{day_of_week, start_time, end_time, label}]
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getCurrentProvider()
      .then((r) => {
        setProvider(r.data);
        const a = r.data?.availability || {};
        setDraft({
          available_days: Array.isArray(a.available_days) ? a.available_days : [0, 1, 2, 3, 4],
          day_windows: a.day_windows || {},
          blocked_times: Array.isArray(a.blocked_times) ? a.blocked_times : [],
        });
      })
      .catch((e) => toast.error(`Could not load provider: ${e.message}`));
  }, []);

  const toggleDay = (d) => {
    setDraft((prev) => {
      const has = prev.available_days.includes(d);
      const next = has
        ? prev.available_days.filter((x) => x !== d)
        : [...prev.available_days, d].sort();
      // If we removed the day, also drop its window override
      const dw = { ...prev.day_windows };
      if (has) delete dw[String(d)];
      return { ...prev, available_days: next, day_windows: dw };
    });
  };

  const setDayWindow = (d, field, value) => {
    setDraft((prev) => {
      const k = String(d);
      const cur = prev.day_windows[k] || { start: "08:00", end: "15:00" };
      return { ...prev, day_windows: { ...prev.day_windows, [k]: { ...cur, [field]: value } } };
    });
  };
  const clearDayWindow = (d) => {
    setDraft((prev) => {
      const dw = { ...prev.day_windows };
      delete dw[String(d)];
      return { ...prev, day_windows: dw };
    });
  };

  const addBlocked = () =>
    setDraft((prev) => ({
      ...prev,
      blocked_times: [
        ...prev.blocked_times,
        { day_of_week: -1, start_time: "11:30", end_time: "12:00", label: "" },
      ],
    }));
  const updateBlocked = (idx, field, value) =>
    setDraft((prev) => ({
      ...prev,
      blocked_times: prev.blocked_times.map((b, i) => (i === idx ? { ...b, [field]: value } : b)),
    }));
  const removeBlocked = (idx) =>
    setDraft((prev) => ({
      ...prev,
      blocked_times: prev.blocked_times.filter((_, i) => i !== idx),
    }));

  // Iter 42 — No immediate fan-out. Multi-day selection stores on
  // `b.days[]` and expands to N per-day rows ONLY at save time. Lets
  // users freely toggle multiple days without the row duplicating
  // mid-selection.
  const expandBlockedForSave = (list) => {
    const out = [];
    for (const b of list || []) {
      const days = Array.isArray(b.days) && b.days.length
        ? b.days
        : [typeof b.day_of_week === "number" ? b.day_of_week : -1];
      if (days.includes(-1)) {
        const { days: _d, ...rest } = b; void _d;
        out.push({ ...rest, day_of_week: -1 });
        continue;
      }
      if (days.length <= 1) {
        const { days: _d, ...rest } = b; void _d;
        out.push({ ...rest, day_of_week: days[0] ?? -1 });
        continue;
      }
      for (const d of days) {
        const { days: _d, ...rest } = b; void _d;
        out.push({ ...rest, day_of_week: d });
      }
    }
    return out;
  };

  const save = async () => {
    if (!provider?.id) return;
    setSaving(true);
    try {
      const payload = { ...draft, blocked_times: expandBlockedForSave(draft.blocked_times) };
      await api.updateProviderAvailability(provider.id, payload);
      // Reflect expanded shape locally so the row list reconciles.
      setDraft(payload);
      toast.success("Provider availability saved");
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!provider) return null;

  return (
    <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm" data-testid="provider-availability-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base font-semibold text-[#1F2937] heading-font">
          Provider Availability
        </CardTitle>
        <Button
          size="sm"
          onClick={save}
          disabled={saving}
          data-testid="provider-avail-save"
          className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg h-8 text-xs"
        >
          <FloppyDisk size={12} weight="bold" className="mr-1" />
          {saving ? "Saving…" : "Save"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* FP3-067 — Provider Availability now scopes ONLY general
            working days + hours. Recurring unavailable time (lunch,
            evals, meetings, admin, duties) lives in the dedicated
            Provider Block Schedule card below — see the helper note. */}
        <p className="text-[11px] text-[#4B5563] leading-snug" data-testid="provider-avail-helper">
          Set the provider's general working days and hours. Meetings,
          lunch, evals, admin time, and other unavailable blocks belong
          in <span className="font-semibold">Provider Block Schedule</span>.
        </p>
        {/* Available days + per-day windows */}
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563] mb-2">
            Available days · click a day to toggle, edit hours per day
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
            {DAY_LABELS.map((d, idx) => {
              const active = draft.available_days.includes(idx);
              const win = draft.day_windows[String(idx)];
              return (
                <div
                  key={d}
                  data-testid={`provider-day-${idx}`}
                  className={`min-w-0 border rounded-lg p-2 transition-colors ${
                    active ? "bg-[#F2F8F4] border-[#D9E5DC]" : "bg-[#F9F8F6] border-[#E5E3DB] opacity-60"
                  }`}
                >
                  <button
                    onClick={() => toggleDay(idx)}
                    data-testid={`provider-day-toggle-${idx}`}
                    className={`w-full text-xs font-semibold py-1 rounded mb-1.5 transition-colors ${
                      active
                        ? "bg-[#1E3F33] text-white"
                        : "bg-white text-[#6B7280] border border-[#d4d2cc]"
                    }`}
                  >
                    {d}
                  </button>
                  {active && (
                    <div className="space-y-1">
                      {/* FP3-075F — Stack the two time inputs vertically so
                          they never overlap when the day-card grid is
                          narrow (sidebar open, smaller laptop / tablet
                          viewport). Browser-native <input type="time"/>
                          has an intrinsic min-width that crashes a
                          side-by-side pair below ~250px / cell. */}
                      <div className="flex flex-col gap-1 min-w-0">
                        <Input
                          type="time"
                          value={win?.start || "08:00"}
                          onChange={(e) => setDayWindow(idx, "start", e.target.value)}
                          disabled={!win}
                          className="h-6 text-[10px] w-full min-w-0 px-1"
                          data-testid={`provider-day-start-${idx}`}
                        />
                        <Input
                          type="time"
                          value={win?.end || "15:00"}
                          onChange={(e) => setDayWindow(idx, "end", e.target.value)}
                          disabled={!win}
                          className="h-6 text-[10px] w-full min-w-0 px-1"
                          data-testid={`provider-day-end-${idx}`}
                        />
                      </div>
                      {/* FP3-067 — Simplified to "Edit" / "Reset" so the
                          control reads as "edit the day's hours" rather
                          than implying the day must match a student
                          school day. */}
                      <button
                        onClick={() =>
                          win
                            ? clearDayWindow(idx)
                            : setDayWindow(idx, "start", "08:00") || setDayWindow(idx, "end", "15:00")
                        }
                        data-testid={`provider-day-window-${idx}`}
                        className="text-[9px] text-[#1E3F33] underline w-full text-left"
                      >
                        {win ? "Reset" : "Edit"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-[10px] text-[#9B9890] italic mt-2">
            Days off (e.g. no Thursdays) won't appear in any wizard run.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
